import { useCallback, useState } from "react";
import { normalizeBlisterSettings } from "../lib/blister-settings";
import { inferFractalFamilyFromPrompt, normalizeFractalSettings } from "../lib/fractal-settings";
import { normalizeKaleidoscopeSettings } from "../lib/kaleidoscope-settings";
import { BlisterSettingsSchema } from "../types/settings.schema";

type AIToolDefinition = {
  type: "function";
  function: {
    name: string;
    parameters: Record<string, unknown>;
  };
};

export function parseAISettingsResponse(content: string) {
  // Find the first {...} block in the response
  const firstBrace = content.indexOf("{");
  const lastBrace = content.lastIndexOf("}");
  if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) {
    console.error("No JSON object found in AI response:", content);
    return null;
  }
  const jsonString = content.slice(firstBrace, lastBrace + 1);
  let parsed: any = null;
  try {
    parsed = JSON.parse(jsonString);
  } catch (e) {
    console.error("AI response is not valid JSON:", e, jsonString);
    return null;
  }
  // Validate with Zod schema
  const result = BlisterSettingsSchema.safeParse(parsed);
  if (result.success) {
    return normalizeBlisterSettings(result.data);
  } else {
    return null;
  }
}

export function useAI() {
  const [aiColors, setAIColors] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(false);

  const requestAISettings = useCallback(async ({
    tool,
    systemPrompt,
    userPrompt,
  }: {
    tool: AIToolDefinition;
    systemPrompt: string;
    userPrompt: string;
  }) => {
    setLoading(true);
    try {
      const body = {
        model: import.meta.env.VITE_GPT_MODEL,
        tools: [tool],
        tool_choice: {
          type: "function",
          function: { name: tool.function.name },
        },
        messages: [
          {
            role: "system",
            content: systemPrompt,
          },
          {
            role: "user",
            content: userPrompt,
          },
        ],
      };
      const res = await fetch("/api/openai-proxy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      const toolCall = data.choices?.[0]?.message?.tool_calls?.[0];
      if (!toolCall || !toolCall.function?.arguments) return null;
      let args: any = {};
      try {
        args = JSON.parse(toolCall.function.arguments);
      } catch {
        return null;
      }
      return args;
    } catch (e) {
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getBlisterAISettings = useCallback(async (prompt: string) => {
    const args = await requestAISettings({
      tool: {
        type: "function",
        function: {
          name: "generate_blister_settings",
          parameters: {
            type: "object",
            properties: {
              colors: {
                type: "array",
                items: { type: "string", pattern: "^#[0-9A-Fa-f]{6}$" },
                minItems: 6,
                maxItems: 6,
                uniqueItems: true,
              },
              particleSize: { type: "number", minimum: 0.2, maximum: 2 },
              speed: { type: "number", minimum: 0.001, maximum: 0.4 },
              count: { type: "integer", minimum: 100, maximum: 5000 },
              opacity: { type: "number", minimum: 0.5, maximum: 1 },
              additive: { type: "boolean" },
              speedRandomness: { type: "number", minimum: 0, maximum: 4 },
              sizeRandomness: { type: "number", minimum: 0, maximum: 4 },
              glowStrength: { type: "number", minimum: 0, maximum: 10 },
              blurStrength: { type: "number", minimum: 0, maximum: 10 },
              glowFraction: { type: "number", minimum: 0, maximum: 1 },
              blurFraction: { type: "number", minimum: 0, maximum: 1 },
              windStrength: { type: "number", minimum: 0, maximum: 2 },
              windFraction: { type: "number", minimum: 0, maximum: 1 },
              atmosphereIntensity: { type: "number", minimum: 0, maximum: 1.4 },
              ambientDriftSpeed: { type: "number", minimum: 0, maximum: 1.2 },
              hazeAmount: { type: "number", minimum: 0, maximum: 1.2 },
              fieldDistortion: { type: "number", minimum: 0, maximum: 1.2 },
              depthSoftness: { type: "number", minimum: 0, maximum: 1.2 },
              sparkleCount: { type: "integer", minimum: 120, maximum: 3200 },
              sparkleSize: { type: "number", minimum: 0.08, maximum: 0.8 },
              sparkleOpacity: { type: "number", minimum: 0.06, maximum: 0.9 },
              sparkleReactivity: { type: "number", minimum: 0, maximum: 1.2 },
              sparkleRandomness: { type: "number", minimum: 0, maximum: 1.2 },
              pulseIntensity: { type: "number", minimum: 0, maximum: 0.8 },
              pulseFrequency: { type: "number", minimum: 0, maximum: 0.85 },
              trailAmount: { type: "number", minimum: 0, maximum: 0.5 },
              afterimageAmount: { type: "number", minimum: 0, maximum: 0.42 },
              flashSoftness: { type: "number", minimum: 0.1, maximum: 1 },
              bloomIntensity: { type: "number", minimum: 0, maximum: 2.2 },
              bloomRadius: { type: "number", minimum: 0, maximum: 1 },
              grainAmount: { type: "number", minimum: 0, maximum: 0.22 },
              vignetteAmount: { type: "number", minimum: 0, maximum: 1 },
              chromaticAberration: { type: "number", minimum: 0, maximum: 0.35 },
              despawnZ: { type: "number" },
              spawnZ: { type: "number" },
            },
            required: [
              "colors",
              "particleSize",
              "speed",
              "count",
              "opacity",
              "additive",
              "speedRandomness",
              "sizeRandomness",
              "glowStrength",
              "blurStrength",
              "glowFraction",
              "blurFraction",
              "windStrength",
              "windFraction",
              "atmosphereIntensity",
              "ambientDriftSpeed",
              "hazeAmount",
              "fieldDistortion",
              "depthSoftness",
              "sparkleCount",
              "sparkleSize",
              "sparkleOpacity",
              "sparkleReactivity",
              "sparkleRandomness",
              "pulseIntensity",
              "pulseFrequency",
              "trailAmount",
              "afterimageAmount",
              "flashSoftness",
              "bloomIntensity",
              "bloomRadius",
              "grainAmount",
              "vignetteAmount",
              "chromaticAberration",
            ],
            additionalProperties: false,
          },
        },
      },
      systemPrompt:
        "You generate settings for Blisters, a soft immersive layered particle scene. Favor dreamy, minimal, calming compositions with tasteful atmosphere, sparkles, and especially restrained pulse motion. Keep values inside safe artistic ranges, avoid noisy or aggressive combinations, avoid harsh additive blowouts in the center, and balance count, bloom, and trails for performance.",
      userPrompt:
        `Generate layered Blister mode settings for: \"${prompt}\". Include atmosphere, main particles, sparkles, reactive pulse behavior, and post-processing controls. Optimize for a calm, efficient default render profile.`,
    });

    if (!args) {
      return null;
    }

    const colors = Array.isArray((args as { colors?: unknown[] }).colors)
      ? ((args as { colors: string[] }).colors).slice(0, 6)
      : [];
    const processed = normalizeBlisterSettings({ ...(args as Record<string, unknown>), colors });
    if (colors.length === 6) {
      setAIColors(colors);
    }
    return processed;
  }, [requestAISettings]);

  const getKaleidoscopeAISettings = useCallback(async (prompt: string) => {
    const args = await requestAISettings({
      tool: {
        type: "function",
        function: {
          name: "generate_kaleidoscope_settings",
          parameters: {
            type: "object",
            properties: {
              colors: {
                type: "array",
                items: { type: "string", pattern: "^#[0-9A-Fa-f]{6}$" },
                minItems: 6,
                maxItems: 6,
              },
              segments: { type: "integer", minimum: 6, maximum: 18 },
              facetDensity: { type: "number", minimum: 0.2, maximum: 1.2 },
              patternComplexity: { type: "number", minimum: 0.2, maximum: 1.2 },
              centerStructure: { type: "number", minimum: 0, maximum: 1.2 },
              outerReflection: { type: "number", minimum: 0, maximum: 1.2 },
              layerDepth: { type: "number", minimum: 0.1, maximum: 1.1 },
              rotationSpeed: { type: "number", minimum: 0, maximum: 1.2 },
              morphSpeed: { type: "number", minimum: 0, maximum: 1.1 },
              shimmerSpeed: { type: "number", minimum: 0, maximum: 1.2 },
              pulseStrength: { type: "number", minimum: 0, maximum: 0.9 },
              driftAmount: { type: "number", minimum: 0, maximum: 1.1 },
              glowStrength: { type: "number", minimum: 0.2, maximum: 1.4 },
              bloomRadius: { type: "number", minimum: 0.08, maximum: 0.7 },
              highlightIntensity: { type: "number", minimum: 0, maximum: 1.2 },
              refractionStrength: { type: "number", minimum: 0, maximum: 0.8 },
              contrast: { type: "number", minimum: 0.2, maximum: 1.2 },
              sparkleDensity: { type: "number", minimum: 0, maximum: 1 },
              microFacetStrength: { type: "number", minimum: 0, maximum: 1.2 },
              textureAmount: { type: "number", minimum: 0, maximum: 1.2 },
              paletteIntensity: { type: "number", minimum: 0.35, maximum: 1.4 },
              colorSeparation: { type: "number", minimum: 0, maximum: 1.2 },
              darkLightBalance: { type: "number", minimum: 0, maximum: 1.2 },
            },
            required: [
              "colors",
              "segments",
              "facetDensity",
              "patternComplexity",
              "centerStructure",
              "outerReflection",
              "layerDepth",
              "rotationSpeed",
              "morphSpeed",
              "shimmerSpeed",
              "pulseStrength",
              "driftAmount",
              "glowStrength",
              "bloomRadius",
              "highlightIntensity",
              "refractionStrength",
              "contrast",
              "sparkleDensity",
              "microFacetStrength",
              "textureAmount",
              "paletteIntensity",
              "colorSeparation",
              "darkLightBalance",
            ],
            additionalProperties: false,
          },
        },
      },
      systemPrompt:
        "You generate settings for Blisters Kaleidoscope mode, a premium crystalline kaleidoscope scene. Favor sharp mirrored shards, stained-glass color regions, jewel-like highlights, structured centers, rich edge reflections, restrained prism refraction, and readable geometry with soft bloom accents. Avoid underwater, blurry, organic, muddy, noisy, or chaotic outputs. Values must stay calm, elegant, performant, and aesthetically coherent.",
      userPrompt:
        `Generate Kaleidoscope mode settings for: \"${prompt}\". Produce a crystalline mirrored scene with structured facet density, readable shard geometry, a defined kaleidoscope center, rich outer reflections, restrained shimmer, subtle prism refraction, strong highlight control, and jewel-like color separation. Optimize for calmness, clarity, beauty, and browser performance.`,
    });

    if (!args) {
      return null;
    }

    const colors = Array.isArray((args as { colors?: unknown[] }).colors)
      ? ((args as { colors: string[] }).colors).slice(0, 6)
      : [];
    const processed = normalizeKaleidoscopeSettings({ ...(args as Record<string, unknown>), colors });
    if (colors.length === 6) {
      setAIColors(colors);
    }
    return processed;
  }, [requestAISettings]);

  const getFractalAISettings = useCallback(async (prompt: string) => {
    const args = await requestAISettings({
      tool: {
        type: "function",
        function: {
          name: "generate_fractal_settings",
          parameters: {
            type: "object",
            properties: {
              colors: {
                type: "array",
                items: { type: "string", pattern: "^#[0-9A-Fa-f]{6}$" },
                minItems: 6,
                maxItems: 6,
              },
              fractalFamily: {
                type: "string",
                enum: ["cathedral", "nebula", "crystal", "mandala", "monolith"],
              },
              intensity: { type: "number", minimum: 0, maximum: 1 },
              brightness: { type: "number", minimum: 0, maximum: 1 },
              contrast: { type: "number", minimum: 0, maximum: 1 },
              scale: { type: "number", minimum: 0, maximum: 1 },
              symmetry: { type: "number", minimum: 0, maximum: 1 },
              glow: { type: "number", minimum: 0, maximum: 1 },
              vignette: { type: "number", minimum: 0, maximum: 1 },
              grain: { type: "number", minimum: 0, maximum: 1 },
              paletteIntensity: { type: "number", minimum: 0, maximum: 1.2 },
              hueShift: { type: "number", minimum: -1, maximum: 1 },
              saturation: { type: "number", minimum: 0, maximum: 1 },
              colorDrift: { type: "number", minimum: 0, maximum: 1 },
              motionSpeed: { type: "number", minimum: 0, maximum: 1 },
              rotationSpeed: { type: "number", minimum: 0, maximum: 1 },
              drift: { type: "number", minimum: 0, maximum: 1 },
              breathing: { type: "number", minimum: 0, maximum: 1 },
              shimmer: { type: "number", minimum: 0, maximum: 1 },
              audioReactive: { type: "boolean" },
              bassImpact: { type: "number", minimum: 0, maximum: 1 },
              midImpact: { type: "number", minimum: 0, maximum: 1 },
              trebleImpact: { type: "number", minimum: 0, maximum: 1 },
              beatPulse: { type: "number", minimum: 0, maximum: 1 },
              audioSmoothing: { type: "number", minimum: 0, maximum: 1 },
              fractalDepth: { type: "number", minimum: 0, maximum: 1 },
              foldStrength: { type: "number", minimum: 0, maximum: 1 },
              repetition: { type: "number", minimum: 0, maximum: 1 },
              fieldWarp: { type: "number", minimum: 0, maximum: 1 },
              detail: { type: "number", minimum: 0, maximum: 1 },
            },
            required: [
              "colors",
              "fractalFamily",
              "intensity",
              "brightness",
              "contrast",
              "scale",
              "symmetry",
              "glow",
              "vignette",
              "grain",
              "paletteIntensity",
              "hueShift",
              "saturation",
              "colorDrift",
              "motionSpeed",
              "rotationSpeed",
              "drift",
              "breathing",
              "shimmer",
              "audioReactive",
              "bassImpact",
              "midImpact",
              "trebleImpact",
              "beatPulse",
              "audioSmoothing",
              "fractalDepth",
              "foldStrength",
              "repetition",
              "fieldWarp",
              "detail",
            ],
            additionalProperties: false,
          },
        },
      },
      systemPrompt:
        "You generate settings for Blisters Fractal mode. You must choose one family from cathedral, nebula, crystal, mandala, or monolith. Cathedral means architectural inner chambers and halo framing. Nebula means softer distributed spatial recursion with less center pressure. Crystal means faceted darker recursion with sharper glints. Mandala means ornamental radial hierarchy with elegant symmetry. Monolith means imposing macro silhouette, sparse premium highlights, and lower symmetry. Keep outputs calm, premium, mathematically structured, and browser-friendly.",
      userPrompt:
        `Generate Fractal mode settings for: \"${prompt}\". Select the best Fractal family for the prompt, then produce a browser-friendly recursive scene with that family's composition grammar, premium highlights, restrained bloom, and a curated Blisters palette. Keep it calm, memorable, and clearly math-based.`,
    });

    if (!args) {
      return null;
    }

    const colors = Array.isArray((args as { colors?: unknown[] }).colors)
      ? ((args as { colors: string[] }).colors).slice(0, 6)
      : [];
    const inferredFamily = inferFractalFamilyFromPrompt(prompt);
    const processed = normalizeFractalSettings({
      fractalFamily: inferredFamily ?? undefined,
      ...(args as Record<string, unknown>),
      colors,
    });
    if (colors.length === 6) {
      setAIColors(colors);
    }
    return processed;
  }, [requestAISettings]);

  return { aiColors, setAIColors, getBlisterAISettings, getFractalAISettings, getKaleidoscopeAISettings, loading };
}
